import { TabPanel, TabPanelProps } from "../../components/TabPanel.tsx";
import { useEffect, useState } from "react";
import { fetchWithError } from "../../utils/fetchWithError.ts";
import {
    Avatar,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Link,
    Stack,
    Typography,
    alpha,
    useTheme
} from "@mui/material";
import { Apps, Explore, InfoOutlined } from "@mui/icons-material";
import { IAccount } from "../../../../src/core/types/user/account.interface";
import { PopulatedAppUser } from "../../../../src/core/types/user/app-user.interface";
import { IProfile } from "@ids/shared";
import { ISecurityInformation } from "../../../../src/core/types/core/security-information.interface";
import { useNavigate } from "react-router-dom";
import { AppCard } from "./AppCard.tsx";

const PASSWORD_MAX_AGE_DAYS = 90;
const FAILED_LOGIN_RECENT_MS = 24 * 60 * 60 * 1000;

interface ClientAppWithUrl {
    name: string;
    url?: string;
    icon?: { data: string; contentType: string };
}

interface AppUserWithClientAppUrl extends Omit<PopulatedAppUser, 'clientApp'> {
    clientApp: ClientAppWithUrl;
}

interface IPublicApplication {
    id: string;
    name: string;
    description?: string;
    url?: string;
}

interface DashboardTabProps extends TabPanelProps {
    refreshKey?: number;
}

function getInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return name.slice(0, 2).toUpperCase() || "?";
}

function getColorFromString(str: string, palette: readonly string[]): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palette[Math.abs(hash) % palette.length];
}

export const DashboardTab = (props: DashboardTabProps) => {
    const { refreshKey, ...tabPanelProps } = props;
    const [appUsers, setAppUsers] = useState<AppUserWithClientAppUrl[]>([]);
    const [profile, setProfile] = useState<IProfile | null>(null);
    const [account, setAccount] = useState<IAccount | null>(null);
    const [securityInfo, setSecurityInfo] = useState<ISecurityInformation | null>(null);
    const [publicApplications, setPublicApplications] = useState<IPublicApplication[]>([]);
    const [showProfileTooltip, setShowProfileTooltip] = useState(true);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
    const navigate = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        loadDashboardData();
    }, [refreshKey]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            const [
                appsRes,
                profileRes,
                accountRes,
                securityRes,
                publicAppsRes,
                settingsRes
            ] = await Promise.all([
                fetchWithError("/session/app-users", { credentials: "include" }).catch(() => []),
                fetchWithError("/profile", { credentials: "include" })
                    .then((r: IProfile & { profile?: IProfile }) => r.profile ?? r)
                    .catch(() => null),
                fetchWithError("/session/account", { credentials: "include" }).catch(() => null),
                fetchWithError("/session/security-info", { credentials: "include" }).catch(() => null),
                fetchWithError("/applications").catch(() => []),
                fetchWithError("/session/settings", { credentials: "include" }).catch(() => null)
            ]);

            const allApps: AppUserWithClientAppUrl[] = (appsRes || []).filter(
                (au: AppUserWithClientAppUrl) => au.clientApp != null
            );
            setAppUsers(allApps);

            setProfile(profileRes ?? null);
            setAccount(accountRes ?? null);
            setSecurityInfo(securityRes ?? null);
            setPublicApplications(Array.isArray(publicAppsRes) ? publicAppsRes : []);
            setShowProfileTooltip(settingsRes?.global?.showProfileTooltip ?? true);
        } catch (error) {
            console.error("Error loading dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const lastLogin = securityInfo?.lastLogin
        ? new Date(securityInfo.lastLogin).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
          })
        : null;
    const lastLoginApp = securityInfo?.lastLoginClientApplication?.name ?? null;
    const shouldShowPasswordAgeHint = !account?.isGoogleLinked;
    const lastPasswordChange = securityInfo?.lastPasswordChange;
    const passwordTooOld = shouldShowPasswordAgeHint && (
        !lastPasswordChange ||
        Date.now() - new Date(lastPasswordChange).getTime() >
            PASSWORD_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
    );
    const hasRecentFailedLogins =
        !!securityInfo?.lastFailedLogin &&
        Date.now() - new Date(securityInfo.lastFailedLogin).getTime() <= FAILED_LOGIN_RECENT_MS;

    const showProfilePhotoUploadTip =
        !!profile &&
        !(profile.avatar || profile.profileImage) &&
        !account?.isGoogleLinked;

    const handleHideProfileTooltip = async () => {
        try {
            await fetchWithError("/session/settings/global", {
                method: "PUT",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ showProfileTooltip: false })
            });
            setShowProfileTooltip(false);
        } catch (e) {
            console.error("Failed to hide profile tooltip", e);
        }
    };

    const myClientIds = appUsers.map((au) => String(au.clientId));
    const discoverableApps = publicApplications.filter((app) => !myClientIds.includes(app.id));

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, appId: string) => {
        setAnchorEl({ ...anchorEl, [appId]: event.currentTarget });
    };

    const handleMenuClose = (appId: string) => {
        setAnchorEl({ ...anchorEl, [appId]: null });
    };

    const handleIconClick = (url?: string) => {
        if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <TabPanel {...tabPanelProps}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 600 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={3}>

                {/* Welcome Section */}
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                        Hallo {profile?.firstname}!
                    </Typography>
                </Box>

                {/* Profile Tooltip */}
                {showProfileTooltip && (
                    <Card
                        variant="subtle"
                        sx={{
                            borderLeft: 4,
                            borderLeftColor: "primary.main",
                            borderRadius: { xs: 0, sm: 2 },
                            boxShadow: (t) =>
                                `0 2px 12px ${alpha(t.palette.primary.main, 0.12)}`
                        }}
                    >
                        <CardContent sx={{ py: 2.5, "&:last-child": { pb: 2.5 } }}>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                                <Box
                                    sx={{
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0
                                    }}
                                >
                                    <InfoOutlined sx={{ fontSize: 28 }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.75 }}>
                                    Schön, dass du da bist! 
                                    </Typography>
                                    <Box
                                        component="ul"
                                        sx={{
                                            m: 0,
                                            mb: 1.5,
                                            pl: 2.25,
                                            color: "text.secondary",
                                            "& li": { mb: 0.75, "&:last-of-type": { mb: 0 } }
                                        }}
                                    >
                                        <Typography component="li" variant="body2">
                                            Du kannst hier dein Profil anpassen.
                                        </Typography>
                                        {showProfilePhotoUploadTip && (
                                            <Typography component="li" variant="body2">
                                                Lade ein Profilbild hoch, damit dich andere erkennen.
                                            </Typography>
                                        )}
                                        <Typography component="li" variant="body2">
                                            Über die Tabs oben erreichst du Nachrichten und
                                            Kontoeinstellungen.
                                        </Typography>
                                    </Box>
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={handleHideProfileTooltip}
                                        sx={{ cursor: "pointer", fontWeight: 500 }}
                                    >
                                        Hinweis ausblenden
                                    </Link>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {/* Neuigkeiten (text) */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Informationen
                    </Typography>
                    <Card
                        variant="subtle"
                        sx={{
                            borderRadius: { xs: 0, sm: 2 }
                        }}
                    >
                        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                            <Stack spacing={1.25}>
                                {lastLogin && (
                                    <Typography variant="body2">
                                        Zuletzt eingeloggt: {lastLogin}
                                        {lastLoginApp ? ` über ${lastLoginApp}` : ""}.
                                    </Typography>
                                )}
                                {passwordTooOld && (
                                    <Typography variant="body2">
                                        Dein Passwort wurde seit über {PASSWORD_MAX_AGE_DAYS} Tagen nicht geändert.{" "}
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={() => navigate("/app/profile?tab=5")}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            Passwort ändern
                                        </Link>
                                    </Typography>
                                )}
                                {hasRecentFailedLogins && (
                                    <Typography variant="body2">
                                        Kürzlich fehlgeschlagene Anmeldungen.{" "}
                                        <Link
                                            component="button"
                                            variant="body2"
                                            onClick={() => navigate("/app/profile?tab=5")}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            Sicherheit prüfen
                                        </Link>
                                    </Typography>
                                )}
                                {!lastLogin &&
                                    !showProfileTooltip &&
                                    !passwordTooOld &&
                                    !hasRecentFailedLogins && (
                                        <Typography variant="body2" color="text.secondary">
                                            Keine offenen Punkte.
                                        </Typography>
                                    )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                {/* Meine Anwendungen */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Meine Anwendungen
                    </Typography>
                    {appUsers.length > 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: { xs: 1.5, sm: 2 },
                                justifyContent: "center",
                                width: "100%",
                                maxWidth: { xs: "100%", sm: "600px", md: "800px" }
                            }}
                        >
                            {appUsers.map((app) => (
                                <AppCard
                                    key={app.id}
                                    app={app}
                                    anchorEl={anchorEl}
                                    onMenuOpen={handleMenuOpen}
                                    onMenuClose={handleMenuClose}
                                    onIconClick={handleIconClick}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Card
                            variant="subtle"
                            sx={{
                                borderRadius: { xs: 0, sm: 2 }
                            }}
                        >
                            <CardContent sx={{ textAlign: "center", py: 4 }}>
                                <Apps sx={{ color: "text.secondary", fontSize: 48, mb: 1.5 }} />
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Noch keine Anwendungen
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Du hast noch keine Anwendungen.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {/* Anwendungen entdecken */}
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Anwendungen entdecken
                    </Typography>
                    {discoverableApps.length > 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: { xs: 1.5, sm: 2 },
                                justifyContent: "center",
                                width: "100%",
                                maxWidth: { xs: "100%", sm: "600px", md: "800px" }
                            }}
                        >
                            {discoverableApps.map((app) => (
                                <Card
                                    key={app.id}
                                    variant="subtle"
                                    sx={{
                                        borderRadius: { xs: 0, sm: 2 },
                                        overflow: "visible",
                                        height: "100%",
                                        width: "100%",
                                        maxWidth: 260,
                                        display: "flex",
                                        flexDirection: "column",
                                        transition:
                                            "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                                        "&:hover": app.url
                                            ? {
                                                  transform: { xs: "none", sm: "translateY(-2px)" },
                                                  boxShadow: (t) =>
                                                      `0 4px 12px ${alpha(t.palette.common.black, 0.15)}`
                                              }
                                            : {}
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => app.url && window.open(app.url, "_blank", "noopener,noreferrer")}
                                        disabled={!app.url}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "stretch",
                                            p: 1.5,
                                            height: "100%",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 1.5,
                                                flex: 1,
                                                minHeight: 100,
                                                width: "100%",
                                                textAlign: "left"
                                            }}
                                        >
                                            <Avatar
                                                src={`/applications/${app.id}/icon`}
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    flexShrink: 0,
                                                    border: 0,
                                                    bgcolor: theme.palette.semantic?.avatarColors
                                                        ? getColorFromString(
                                                              app.name,
                                                              theme.palette.semantic.avatarColors
                                                          )
                                                        : "action.hover",
                                                    fontSize: "1rem",
                                                    fontWeight: 600,
                                                    p: 0,
                                                    borderRadius: 2,
                                                    "& img": {
                                                        objectFit: "contain"
                                                    }
                                                }}
                                            >
                                                {getInitials(app.name)}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        lineHeight: 1.4,
                                                        wordBreak: "break-word"
                                                    }}
                                                >
                                                    {app.name}
                                                </Typography>
                                                {app.description && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                            lineHeight: 1.3,
                                                            mt: 0.25
                                                        }}
                                                    >
                                                        {app.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <Card
                            variant="subtle"
                            sx={{
                                borderRadius: { xs: 0, sm: 2 }
                            }}
                        >
                            <CardContent sx={{ textAlign: "center", py: 4 }}>
                                <Explore sx={{ color: "text.secondary", fontSize: 48, mb: 1.5 }} />
                                <Typography variant="body1" color="text.secondary" gutterBottom>
                                    Keine weiteren Apps zum Entdecken
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Du nutzt bereits alle verfügbaren Anwendungen.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
                </Stack>
            )}
        </TabPanel>
    );
};
